/**
 * Converts a Firestore Timestamp, Date, or ISO string to a JS Date.
 * Firestore SDK returns Timestamp objects that have a `.toDate()` method.
 * When data comes from a JSON-serialized source, it may be an ISO string.
 */
interface FirestoreTimestamp {
  toDate(): Date;
}

function isFirestoreTimestamp(value: unknown): value is FirestoreTimestamp {
  return (
    value !== null &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as Record<string, unknown>).toDate === 'function'
  );
}

/**
 * Converts a required Firestore field (Timestamp | string | Date) to a JS Date.
 * Throws if the value is null/undefined.
 */
export function toDate(value: unknown): Date {
  if (isFirestoreTimestamp(value)) return value.toDate();
  if (value instanceof Date) return value;
  return new Date(value as string);
}

/**
 * Converts an optional Firestore field (Timestamp | string | Date | null | undefined)
 * to a JS Date. Returns undefined if the field is absent.
 */
export function toOptionalDate(value: unknown): Date | undefined {
  if (value == null) return undefined;
  return toDate(value);
}
